import React, { useEffect, useState } from 'react';
import ConnectionAvatar from '../integrations/ConnectionAvatar';
import { startXeroReauth } from '../../utils/api_integrations';
import './xero-reauth.scss';

// ---------------------------------------------------------------- #
// XeroReauthModal
// ---------------------------------------------------------------- #
//
// Xero re-authorisation, a one-off for Xero's move to granular scopes.
//
// Xero retires the broad Accounting API scopes on 13 September 2027. Tenants authorised before
// 1 October 2026 hold only the broad scopes, so their admins are asked to send each organisation
// through Xero's consent screen once. The connection is removed first, because Xero will not let
// an already connected organisation be selected. Mappings and settings are not touched.
//
// This modal lists the organisations still to do, with a "Re-authorise" button on each, and shows
// the result after a trip to Xero. XeroReauthPrompt.jsx decides when it opens.
//
// This file can be removed once every active tenant is re-authorised, that is when this returns 0:
//
//   SELECT COUNT(*) FROM ParkiXero.xero_integrations
//    WHERE isActive = '1' AND ( dateAuthorised IS NULL OR dateAuthorised < '2026-10-01' )
//      AND entityId NOT IN ( 11, 77 );
//
// To remove it: delete this folder and the lines marked "remove with xero-reauth" in the React app,
// then parki-api/integrations/XeroReauthorisation.php and every line in parki-api/integrations/APIResponse.php
// marked "remove with XeroReauthorisation.php".
//
// Props:
//   tenants          The organisations to re-authorise, or null when the list could not be loaded
//   canReauthorise   False for staff viewing as the customer: the buttons are disabled
//   result           What came back from Xero ({ status, reason, tenant, ticked }), or null
//   onClose          Called when the user closes the modal

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The text for any failure without a message of its own, and for a failed start call. */
const GENERIC_ERROR_MESSAGE = 'Something went wrong with Xero. Try again, or contact Parki support if it keeps happening.';

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * The plain-English message for a failed trip to Xero.
 */
const buildErrorMessage = (reason, ticked) => {
  // Each reason the API sends has its own wording; anything else gets the generic message
  switch (reason) {
    case 'cancelled':
      return "Xero was cancelled, so this organisation is still disconnected and its data won't update. Re-authorise it to reconnect.";
    case 'not-your-organisation':
      return `You ticked ${ticked || 'an organisation'}, which is not one of your Parki organisations. Try again and tick one of the organisations below.`;
    case 'tenant-belongs-to-another-account':
      return 'That Xero organisation is connected to a different Parki account. Contact Parki support.';
    case 'no-organisation':
      return "Xero didn't connect an organisation. Try again and tick the one you chose. If Xero shows it as already connected, remove Parki from Connected Apps in Xero first.";
    case 'state-invalid':
      return 'That sign-in link had expired. Try again.';
    default:
      return GENERIC_ERROR_MESSAGE;
  }
};

// ---------------------------------------------------------------- #
// The modal
// ---------------------------------------------------------------- #

/**
 * The list of Xero organisations to re-authorise, with the result of the last trip to Xero.
 */
const XeroReauthModal = ({ tenants, canReauthorise, result, onClose }) => {
  // The row whose button is waiting on the start call, and any error from that call
  const [busyTenantId, setBusyTenantId] = useState(null);
  const [startError, setStartError] = useState(null);

  // ---------------------------------------------------------------- #
  // Closing
  // ---------------------------------------------------------------- #

  // Close on the Escape key, the same as the integrations wizards
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  });

  // Stop the page behind the modal from scrolling while it is open
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  // ---------------------------------------------------------------- #
  // Starting a re-authorisation
  // ---------------------------------------------------------------- #

  /**
   * Ask the API to disconnect this organisation and give us Xero's sign-in address, then go there.
   */
  const handleReauthorise = async (bdoXeroId) => {
    // Show the spinner on this row and clear any earlier error
    setBusyTenantId(bdoXeroId);
    setStartError(null);

    // Ask for the address; the API logs anything that goes wrong
    let authoriseUrl = null;
    try {
      const response = await startXeroReauth(bdoXeroId);
      authoriseUrl = response.data?.authoriseUrl || null;
    } catch (err) {
      authoriseUrl = null;
    }

    // No address: tell the user and let them try again
    if (!authoriseUrl) {
      setStartError(GENERIC_ERROR_MESSAGE);
      setBusyTenantId(null);
      return;
    }

    // Leave for Xero, keeping the spinner on until the page changes
    window.location.href = authoriseUrl;
  };

  // ---------------------------------------------------------------- #
  // What to show
  // ---------------------------------------------------------------- #

  // Which parts of the modal apply
  const tenantList = tenants || [];
  const hasTenants = tenantList.length > 0;
  const listLoaded = tenants !== null;
  const isDone = Boolean(result) && result.status === 'done';
  const showAllDone = isDone && listLoaded && !hasTenants;
  const showStaffNote = hasTenants && !canReauthorise;

  // The banner at the top: a failed start call first, otherwise the result from Xero
  let bannerClass = null;
  let bannerText = null;
  if (startError) {
    bannerClass = 'alert alert-danger';
    bannerText = startError;
  } else if (isDone) {
    bannerClass = 'alert alert-success';
    bannerText = `${result.tenant || 'Your Xero organisation'} is re-authorised`;
  } else if (result) {
    bannerClass = 'alert alert-danger';
    bannerText = buildErrorMessage(result.reason, result.ticked);
  }

  // The footer button is the reminder while there is work left, otherwise a plain close
  let closeLabel = 'Close';
  if (hasTenants) {
    closeLabel = 'Remind me next time';
  }

  // Any row waiting on the start call disables every button
  const isBusy = busyTenantId !== null;

  // One table row per organisation
  const tenantRows = [];
  for (const tenant of tenantList) {
    const parkNames = tenant.parkNames || [];
    const isThisRowBusy = busyTenantId === tenant.bdoXeroId;
    tenantRows.push(
      <tr key={tenant.bdoXeroId}>
        <td>
          <div className="xero-reauth-organisation">
            <ConnectionAvatar name={tenant.tenantName} />
            <span>{tenant.tenantName}</span>
          </div>
        </td>
        <td>{parkNames.join(', ')}</td>
        <td className="xero-reauth-action">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => handleReauthorise(tenant.bdoXeroId)}
            disabled={!canReauthorise || isBusy}
          >
            {isThisRowBusy && (
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            )}
            Re-authorise
          </button>
        </td>
      </tr>
    );
  }

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <>
      <div className="modal fade show xero-reauth-modal" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true" aria-label="Re-authorise Xero">
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-fullscreen-md-down" role="document">
          <div className="modal-content">

            {/* Header: title and the theme's close button */}
            <div className="modal-header">
              <h5 className="modal-title mb-0">Re-authorise Xero</h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
            </div>

            <div className="modal-body">
              {/* Result banner */}
              {bannerText && (
                <div className={bannerClass} role="alert">{bannerText}</div>
              )}

              {/* Nothing left to do */}
              {showAllDone && (
                <p className="mb-0">All your Xero organisations are re-authorised</p>
              )}

              {/* The explanation and the list, while there is work left */}
              {hasTenants && (
                <>
                  <p>
                    Xero is changing its app permissions. Please re-authorise each organisation below by <strong>13 September 2027</strong> so your reports keep updating.
                  </p>

                  {/* Staff viewing as the customer cannot start the sign-in */}
                  {showStaffNote && (
                    <div className="alert alert-warning" role="alert">
                      You are viewing as the customer. Ask the customer to re-authorise, because Xero needs their own sign-in.
                    </div>
                  )}

                  {/* One row per organisation */}
                  <div className="table-responsive">
                    <table className="table xero-reauth-table">
                      <thead>
                        <tr>
                          <th scope="col">Organisation</th>
                          <th scope="col">Parks</th>
                          <th scope="col"><span className="visually-hidden">Action</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {tenantRows}
                      </tbody>
                    </table>
                  </div>

                  {/* A quiet reminder of what the button does, for the people who can press it */}
                  {!showStaffNote && (
                    <p className="xero-reauth-note">
                      <i className="icon-base ti tabler-info-circle" aria-hidden="true"></i>
                      <span>Re-authorise disconnects the organisation until you finish on Xero.</span>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Footer: closing counts as "Remind me next time" */}
            <div className="modal-footer">
              <button type="button" className="btn btn-label-secondary" onClick={onClose}>
                {closeLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default XeroReauthModal;
